import { useCallback, useEffect, useMemo, useState } from 'react'
import { AlertCircle, Clock, Download, File, Folder, History, Loader2, X } from 'lucide-react'

import CodeMirrorEditor from './CodeMirrorEditor'
import { useWorkspace } from '../hooks/useWorkspace'
import { useIsMobile } from '../hooks/useIsMobile'
import { formatExactTime, timeAgo } from '../utils/writerUtils'
import { isTextLike } from '../utils/isTextLike'
import type { CommitSummary, FileStat } from '../engine/types'
import './SnapshotBrowserPanel.css'

type PreviewState =
    | { kind: 'idle' }
    | { kind: 'loading' }
    | { kind: 'error'; message: string }
    | { kind: 'text'; content: string }
    | { kind: 'image'; url: string }
    | { kind: 'binary' }

interface SnapshotBrowserPanelProps {
    isOpen: boolean
    commit: CommitSummary | null
    onClose: () => void
}

function commitAuthorLabel(commit: CommitSummary): string {
    const name = commit.writerName?.trim()
    if (name) return name
    return commit.writerKid.slice(0, 8)
}

function commitTimeLabel(commit: CommitSummary): string {
    if (!commit.time) return ''
    return timeAgo(commit.time, { maxDepth: 'days', suffix: ' ago' })
}

function normalizeDirPath(path: string): string {
    if (!path || path === '/') return '/'
    const normalized = path.replace(/\\/g, '/').replace(/\/+$/, '')
    return normalized.startsWith('/') ? normalized : `/${normalized}`
}

function parentDirPath(path: string): string | null {
    const normalized = normalizeDirPath(path)
    if (normalized === '/') return null
    const idx = normalized.lastIndexOf('/')
    return idx <= 0 ? '/' : normalized.slice(0, idx)
}

function entryName(path: string): string {
    const normalized = path.replace(/\/+$/, '')
    const parts = normalized.split('/').filter(Boolean)
    return parts.length > 0 ? parts[parts.length - 1]! : '/'
}

function formatSize(size: number): string {
    if (size < 1024) return `${size} B`
    if (size < 1024 * 1024) return `${Math.round(size / 102.4) / 10} KB`
    return `${Math.round(size / 1024 / 102.4) / 10} MB`
}

function isImageFile(entry: FileStat): boolean {
    return entry.mime.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp|svg|avif|ico)$/i.test(entry.path)
}

export default function SnapshotBrowserPanel({ isOpen, commit, onClose }: SnapshotBrowserPanelProps) {
    const workspace = useWorkspace()
    const isMobile = useIsMobile()
    const [currentDir, setCurrentDir] = useState('/')
    const [entries, setEntries] = useState<FileStat[]>([])
    const [entriesLoading, setEntriesLoading] = useState(false)
    const [entriesError, setEntriesError] = useState<string | null>(null)
    const [selectedEntry, setSelectedEntry] = useState<FileStat | null>(null)
    const [preview, setPreview] = useState<PreviewState>({ kind: 'idle' })

    useEffect(() => {
        if (!isOpen || !commit) return
        setCurrentDir('/')
        setSelectedEntry(null)
        setPreview({ kind: 'idle' })
    }, [isOpen, commit?.commitHash])

    useEffect(() => {
        if (!isOpen || !commit) return

        let cancelled = false
        setEntriesLoading(true)
        setEntriesError(null)

        workspace.history.listDirAtCommit(currentDir, commit.commitHash).then(
            (nextEntries) => {
                if (cancelled) return
                setEntries(nextEntries)
            },
            (error) => {
                if (cancelled) return
                setEntries([])
                setEntriesError(error instanceof Error ? error.message : 'Could not load snapshot directory.')
            },
        ).finally(() => {
            if (!cancelled) setEntriesLoading(false)
        })

        return () => {
            cancelled = true
        }
    }, [workspace, isOpen, commit, currentDir])

    useEffect(() => {
        if (!isOpen || !commit || !selectedEntry) {
            setPreview({ kind: 'idle' })
            return
        }

        let cancelled = false
        let objectUrl: string | null = null
        setPreview({ kind: 'loading' })

        const loadPreview = async () => {
            if (isTextLike(selectedEntry.path, selectedEntry.mime)) {
                const text = await workspace.history.readTextAtCommit(selectedEntry.path, commit.commitHash)
                if (cancelled) return
                if (text !== null) {
                    setPreview({ kind: 'text', content: text })
                    return
                }
            }

            const bytes = await workspace.history.readBytesAtCommit(selectedEntry.path, commit.commitHash)
            if (cancelled) return
            if (!bytes) {
                setPreview({ kind: 'error', message: 'Previous version is not available.' })
                return
            }

            if (isImageFile(selectedEntry)) {
                objectUrl = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: selectedEntry.mime || 'application/octet-stream' }))
                setPreview({ kind: 'image', url: objectUrl })
                return
            }

            setPreview({ kind: 'binary' })
        }

        loadPreview().catch((error) => {
            if (!cancelled) {
                setPreview({
                    kind: 'error',
                    message: error instanceof Error ? error.message : 'Could not load snapshot preview.',
                })
            }
        })

        return () => {
            cancelled = true
            if (objectUrl) URL.revokeObjectURL(objectUrl)
        }
    }, [workspace, isOpen, commit, selectedEntry])

    const sortedEntries = useMemo(() => {
        return [...entries].sort((a, b) => {
            if (a.kind !== b.kind) return a.kind === 'directory' ? -1 : 1
            return a.path.localeCompare(b.path)
        })
    }, [entries])

    const breadcrumbParts = useMemo(() => {
        if (currentDir === '/') return [{ label: '/', path: '/' }]
        const parts = currentDir.split('/').filter(Boolean)
        return [
            { label: '/', path: '/' },
            ...parts.map((part, index) => ({
                label: part,
                path: '/' + parts.slice(0, index + 1).join('/'),
            })),
        ]
    }, [currentDir])

    const handleOpenDir = useCallback((path: string) => {
        setCurrentDir(normalizeDirPath(path))
        setSelectedEntry(null)
        setPreview({ kind: 'idle' })
    }, [])

    const handleDownload = useCallback(async () => {
        if (!commit || !selectedEntry) return
        const bytes = await workspace.history.readBytesAtCommit(selectedEntry.path, commit.commitHash)
        if (!bytes) return

        const url = URL.createObjectURL(new Blob([new Uint8Array(bytes)], { type: selectedEntry.mime || 'application/octet-stream' }))
        const anchor = document.createElement('a')
        anchor.href = url
        anchor.download = entryName(selectedEntry.path)
        anchor.click()
        URL.revokeObjectURL(url)
    }, [workspace, commit, selectedEntry])

    if (!isOpen || !commit) return null

    return (
        <>
            <div className="snapshot-browser-backdrop" onClick={onClose} />
            <div className={`snapshot-browser-panel ${isMobile ? 'mobile' : ''}`}>
                <div className="snapshot-browser-header">
                    <div>
                        <h2><History size={18} /> Snapshot browser</h2>
                        <div className="snapshot-browser-meta">
                            <span>{commitAuthorLabel(commit)}</span>
                            <span>·</span>
                            <span title={commit.time ? formatExactTime(commit.time) : undefined}>
                                {commitTimeLabel(commit) || 'Unknown time'}
                            </span>
                            <span>·</span>
                            <code>{commit.commitHash.slice(0, 8)}</code>
                        </div>
                    </div>
                    <button type="button" className="btn btn-icon" onClick={onClose} aria-label="Close snapshot browser">
                        <X size={18} />
                    </button>
                </div>

                <div className="snapshot-browser-layout">
                    <aside className="snapshot-browser-sidebar">
                        <div className="snapshot-browser-breadcrumbs">
                            {breadcrumbParts.map((part) => (
                                <button
                                    key={part.path}
                                    type="button"
                                    className={`snapshot-browser-crumb ${part.path === currentDir ? 'current' : ''}`}
                                    onClick={() => handleOpenDir(part.path)}
                                >
                                    <span>{part.label}</span>
                                </button>
                            ))}
                        </div>

                        <div className="snapshot-browser-tree">
                            {currentDir !== '/' && (
                                <button
                                    type="button"
                                    className="tree-item tree-dir snapshot-tree-item snapshot-tree-up"
                                    onClick={() => handleOpenDir(parentDirPath(currentDir) ?? '/')}
                                >
                                    <Folder size={16} />
                                    <span className="tree-label">..</span>
                                </button>
                            )}

                            {entriesLoading && (
                                <div className="snapshot-browser-empty">
                                    <Loader2 size={18} className="spin" />
                                    <span>Loading snapshot…</span>
                                </div>
                            )}

                            {!entriesLoading && entriesError && (
                                <div className="snapshot-browser-empty error">
                                    <AlertCircle size={18} />
                                    <span>{entriesError}</span>
                                </div>
                            )}

                            {!entriesLoading && !entriesError && sortedEntries.length === 0 && (
                                <div className="snapshot-browser-empty">
                                    <Folder size={18} />
                                    <span>Empty directory</span>
                                </div>
                            )}

                            {!entriesLoading && !entriesError && sortedEntries.map((entry) => {
                                const label = entryName(entry.path)
                                if (entry.kind === 'directory') {
                                    return (
                                        <button
                                            key={entry.path}
                                            type="button"
                                            className="tree-item tree-dir snapshot-tree-item"
                                            onClick={() => handleOpenDir(entry.path)}
                                        >
                                            <Folder size={16} />
                                            <span className="tree-label">{label}</span>
                                        </button>
                                    )
                                }

                                return (
                                    <button
                                        key={entry.path}
                                        type="button"
                                        className={`tree-item tree-file snapshot-tree-item ${selectedEntry?.path === entry.path ? 'selected' : ''}`}
                                        onClick={() => setSelectedEntry(entry)}
                                    >
                                        <File size={16} />
                                        <span className="tree-label">{label}</span>
                                    </button>
                                )
                            })}
                        </div>
                    </aside>

                    <section className="snapshot-browser-preview">
                        {!selectedEntry && (
                            <div className="snapshot-preview-empty">
                                <Clock size={20} />
                                <span>Select a file from the snapshot to preview it.</span>
                            </div>
                        )}

                        {selectedEntry && (
                            <>
                                <div className="snapshot-preview-header">
                                    <div>
                                        <h3>{entryName(selectedEntry.path)}</h3>
                                        <div className="snapshot-preview-path">{selectedEntry.path}</div>
                                    </div>
                                    <div className="snapshot-preview-actions">
                                        <span className="snapshot-preview-kind">
                                            {selectedEntry.mime || selectedEntry.kind}
                                        </span>
                                        <button type="button" className="btn btn-secondary" onClick={() => void handleDownload()}>
                                            <Download size={14} /> Download
                                        </button>
                                    </div>
                                </div>

                                <div className="snapshot-preview-body">
                                    {preview.kind === 'loading' && (
                                        <div className="snapshot-preview-empty">
                                            <Loader2 size={20} className="spin" />
                                            <span>Loading historical file…</span>
                                        </div>
                                    )}

                                    {preview.kind === 'error' && (
                                        <div className="snapshot-preview-empty error">
                                            <AlertCircle size={20} />
                                            <span>{preview.message}</span>
                                        </div>
                                    )}

                                    {preview.kind === 'text' && (
                                        <div className="snapshot-preview-editor">
                                            <CodeMirrorEditor
                                                content={preview.content}
                                                fontMode="mono"
                                                showLineNumbers
                                                isMobile={isMobile}
                                                blameLines={[]}
                                                blameEnabled={false}
                                                blameStale={false}
                                                blameWidth={180}
                                                readOnly
                                                onChange={() => {}}
                                            />
                                        </div>
                                    )}

                                    {preview.kind === 'image' && (
                                        <div className="snapshot-preview-image">
                                            <img src={preview.url} alt={entryName(selectedEntry.path)} />
                                        </div>
                                    )}

                                    {preview.kind === 'binary' && (
                                        <div className="snapshot-preview-empty">
                                            <File size={20} />
                                            <span>Binary snapshot</span>
                                            <span className="snapshot-preview-subtle">
                                                {selectedEntry.mime || 'application/octet-stream'} · {formatSize(selectedEntry.size)}
                                            </span>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </section>
                </div>
            </div>
        </>
    )
}
